import { getCurrentPrimCoreUrl } from './ApiServices';
import { headers } from './genericConstantes';
import {
	Address,
	AddressFilters,
	AddressPayload,
	AdministrationPaginatedResult,
	Country,
	Supplier,
	SupplierFilters,
	SupplierGroup,
	SupplierGroupFilters,
	SupplierGroupPayload,
	SupplierPayload,
} from '../models/supplierAdministration';

type QueryValue = string | number | boolean | null | undefined;

type ApiErrorBody = {
	message?: string | string[];
	error?: string | string[];
	statusCode?: number;
}

export class SupplierAdministrationApiError extends Error {
	statusCode?: number;

	constructor(message: string, statusCode?: number) {
		super(message);
		this.name = 'SupplierAdministrationApiError';
		this.statusCode = statusCode;
	}
}

const buildQueryString = (params: Record<string, QueryValue>) => {
	const query = new URLSearchParams();

	Object.entries(params).forEach(([key, value]) => {
		if (value === undefined || value === null || value === '') return;
		query.append(key, String(value));
	});

	const serialized = query.toString();
	return serialized ? `?${serialized}` : '';
};

const normalizeErrorMessage = (body: ApiErrorBody | null, fallback: string) => {
	if (!body) return fallback;
	if (Array.isArray(body.message)) return body.message.join(', ');
	if (body.message) return body.message;
	if (Array.isArray(body.error)) return body.error.join(', ');
	if (body.error) return body.error;
	return fallback;
};

const primCoreHeaders = () => ({
	...headers('Bearer'),
});

const requestApi = async <T>(path: string, init: RequestInit = {}, fallbackError = 'Une erreur est survenue.'): Promise<T> => {
	const response = await fetch(`${getCurrentPrimCoreUrl()}${path}`, {
		...init,
		headers: {
			...primCoreHeaders(),
			...(init.headers || {}),
		},
	});

	if (response.status === 204) return undefined as T;

	const body = await response.json().catch(() => null);

	if (!response.ok) {
		throw new SupplierAdministrationApiError(normalizeErrorMessage(body, fallbackError), response.status);
	}

	return body as T;
};

export const supplierAdministrationApi = {
	listSupplierGroups(filters: SupplierGroupFilters) {
		return requestApi<AdministrationPaginatedResult<SupplierGroup>>(
			`/supplier-groups${buildQueryString(filters)}`,
			{ method: 'GET' },
			'Erreur lors de la récupération des groupes fournisseurs.'
		);
	},

	createSupplierGroup(payload: SupplierGroupPayload) {
		return requestApi<SupplierGroup>(
			'/supplier-groups',
			{ method: 'POST', body: JSON.stringify(payload) },
			'Erreur lors de la création du groupe fournisseur.'
		);
	},

	updateSupplierGroup(id: number, payload: SupplierGroupPayload) {
		return requestApi<SupplierGroup>(
			`/supplier-groups/${id}`,
			{ method: 'PUT', body: JSON.stringify(payload) },
			'Erreur lors de la modification du groupe fournisseur.'
		);
	},

	disableSupplierGroup(id: number) {
		return requestApi<SupplierGroup>(
			`/supplier-groups/${id}/disable`,
			{ method: 'PUT' },
			'Erreur lors de la désactivation du groupe fournisseur.'
		);
	},

	enableSupplierGroup(id: number) {
		return requestApi<SupplierGroup>(
			`/supplier-groups/${id}/enable`,
			{ method: 'PUT' },
			'Erreur lors de la réactivation du groupe fournisseur.'
		);
	},

	listSuppliers(filters: SupplierFilters) {
		return requestApi<AdministrationPaginatedResult<Supplier>>(
			`/suppliers${buildQueryString(filters)}`,
			{ method: 'GET' },
			'Erreur lors de la récupération des fournisseurs.'
		);
	},

	createSupplier(payload: SupplierPayload) {
		return requestApi<Supplier>(
			'/suppliers',
			{ method: 'POST', body: JSON.stringify(payload) },
			'Erreur lors de la création du fournisseur.'
		);
	},

	updateSupplier(id: number, payload: SupplierPayload) {
		return requestApi<Supplier>(
			`/suppliers/${id}`,
			{ method: 'PUT', body: JSON.stringify(payload) },
			'Erreur lors de la modification du fournisseur.'
		);
	},

	disableSupplier(id: number) {
		return requestApi<Supplier>(
			`/suppliers/${id}/disable`,
			{ method: 'PUT' },
			'Erreur lors de la désactivation du fournisseur.'
		);
	},

	enableSupplier(id: number) {
		return requestApi<Supplier>(
			`/suppliers/${id}/enable`,
			{ method: 'PUT' },
			'Erreur lors de la réactivation du fournisseur.'
		);
	},

	listAddresses(filters: AddressFilters) {
		return requestApi<AdministrationPaginatedResult<Address>>(
			`/addresses${buildQueryString(filters)}`,
			{ method: 'GET' },
			'Erreur lors de la récupération des adresses.'
		);
	},

	getAddress(id: number) {
		return requestApi<Address>(
			`/addresses/${id}`,
			{ method: 'GET' },
			"Erreur lors de la récupération de l'adresse."
		);
	},

	createAddress(payload: AddressPayload) {
		return requestApi<Address>(
			'/addresses',
			{ method: 'POST', body: JSON.stringify(payload) },
			"Erreur lors de la création de l'adresse."
		);
	},

	updateAddress(id: number, payload: AddressPayload) {
		return requestApi<Address>(
			`/addresses/${id}`,
			{ method: 'PUT', body: JSON.stringify(payload) },
			"Erreur lors de la modification de l'adresse."
		);
	},

	deleteAddress(id: number) {
		return requestApi<void>(
			`/addresses/${id}`,
			{ method: 'DELETE' },
			"Erreur lors de la suppression de l'adresse."
		);
	},

	listCountries() {
		return requestApi<Country[]>(
			'/countries',
			{ method: 'GET' },
			'Erreur lors de la récupération du référentiel des pays.'
		);
	},
};
